import { debounce } from "lodash";
import { ChangeEvent, KeyboardEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import TextInput from "@krea/customer-features/shared-components/fields/input";
import {
  useIsFieldRequestSubmitting,
  usePostFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import { FormatNumber } from "@krea/customer-features/utils";
import twMerge from "@krea/customer-features/utils/tailwind-utils";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

interface IProps {
  fieldRequestID: string;
  withInputLabel: boolean;
  isPreviewMode: boolean;
}

const SelfEstimatedHistoricRevenue = ({
  fieldRequestID,
  withInputLabel,
  isPreviewMode,
}: IProps) => {
  const { t } = useTranslation();

  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const { data: currentFieldRequest } = useGetOneFieldRequest<string | null>(
    fieldRequestID,
  );

  const { mutate: postFieldRequest, error: err } =
    usePostFieldRequest(fieldRequestID);

  const error = err?.data.errors[0];

  const isCurrentFRSubmitting = useIsFieldRequestSubmitting(fieldRequestID);

  const [touched, setTouched] = useState(false);

  const [value, setValue] = useState(
    currentFieldRequest?.submittedData
      ? parseInt(currentFieldRequest.submittedData)
      : null,
  );

  const onPostValueFn = (newValue: number | null) => {
    setTouched(false);
    postFieldRequest({ value: newValue });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onPostValueFn(value);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((newValue: number | null) => onPostValueFn(newValue), 500),
    [],
  );

  const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseInt(target.value.replace(/\s+/g, ""), 10);
    const newValue = Number.isInteger(parsedValue) ? parsedValue : null;

    setTouched(true);
    setValue(newValue);
    debouncedSave(newValue);
  };

  const onBlur = () => {
    if (touched) {
      debouncedSave.cancel();
      onPostValueFn(value);
    }
  };

  return isPreviewMode ? (
    <FieldRequestPreviewHeader
      label={currentFieldRequest?.displayName}
      value={formattedCurrencyValue(value)}
      className=""
    />
  ) : (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className={twMerge("input-form", {
          "input-form-padded": isPreviewMode,
        })}
        onKeyDown={onKeyDown}
        onSubmit={(e) => e.preventDefault()}
      >
        <TextInput
          type="text"
          pattern="[0-9]*"
          maxLength={15}
          inputMode="numeric"
          id={`input_${currentFieldRequest?.field}`}
          value={FormatNumber(value)}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={t("iaq.amount.valuePlaceholder")}
          textAppend={currencyValue}
          label={withInputLabel && currentFieldRequest?.displayName}
          error={error?.message}
        />

        <FieldRequestSaved
          fieldRequestID={fieldRequestID}
          isSubmitting={!!isCurrentFRSubmitting}
          isSubmitted={currentFieldRequest?.isSubmitted}
        />
      </form>
    </>
  );
};

export default SelfEstimatedHistoricRevenue;
